import { ToursList } from '@/components/tours';
import { type Attraction } from '@/config/objects/attractions';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import { useApiClient, type CrudConfig } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function AttractionToursTab() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();

  const { data: attractionData } = useQuery<{ success: boolean; data: Attraction }>({
    queryKey: ['Attraction', id],
    queryFn: () => apiClient.get(`/attractions/${id}`),
    enabled: !!id,
  });

  const attraction = attractionData?.data;

  const attractionToursConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/attractions/${id}/tours`,
        delete: (tourId: string) => `/attractions/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/tours',
            where: attraction
              ? {
                  greaterAreaId: attraction.greaterAreaId,
                  tours: {
                    none: {
                      attractionId: attraction.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (tour: Tour) => {
              await apiClient.post(`/attractions/${id}/tours`, { tourId: tour.id });
              toast.success('Tour added to attraction');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/attractions/${id}/tours/${tour.id}`);
              toast.success('Tour removed from attraction');
            },
          },
        },
      },
    }),
    [attraction, id, apiClient],
  );

  if (!id) return null;

  return <ToursList config={attractionToursConfig} className="p-0" baseUrl="/tours" />;
}
